import { useEffect, useState } from "react";
import { combineDateAndTime } from "../lib/date";
import { displayPhone, normalizePhone } from "../lib/search";
import { loadData, saveData } from "../lib/storage";
import type { AppData, Appointment, AppointmentFormValues, AppointmentStatus, Id, Owner, Pet } from "../types";

function createAppointmentRecord(petId: Id, values: AppointmentFormValues): Appointment {
  const start = combineDateAndTime(values.date, values.startTime);
  const end = new Date(start.getTime() + values.durationMinutes * 60_000);

  return {
    id: crypto.randomUUID(),
    petId,
    startsAt: start.toISOString(),
    endsAt: end.toISOString(),
    status: "pending",
    serviceType: values.serviceType || "other",
    customServiceName: values.serviceType === "other" ? values.customServiceName.trim() || undefined : undefined,
    servicePricePln: values.servicePricePln === "" ? undefined : Number(values.servicePricePln),
    notes: values.appointmentNotes.trim() || undefined,
  };
}

function patchOwner(owner: Owner, values: AppointmentFormValues): Owner {
  return {
    ...owner,
    fullName: values.ownerName.trim(),
    phone: displayPhone(values.phone),
    email: values.email.trim() || undefined,
  };
}

function patchPet(pet: Pet, ownerId: Id, values: AppointmentFormValues): Pet {
  return {
    ...pet,
    ownerId,
    name: values.petName.trim(),
    sex: values.petSex,
    breed: values.breed.trim() || undefined,
    careNotes: values.petNotes.trim() || undefined,
  };
}

export function useAppData() {
  const [data, setData] = useState<AppData>(() => loadData(new Date()));

  useEffect(() => {
    saveData(data);
  }, [data]);

  function createAppointment(values: AppointmentFormValues) {
    const normalizedPhone = normalizePhone(values.phone);
    let createdAppointment: Appointment | null = null;

    setData((current) => {
      if (values.existingPetId) {
        const selectedPet = current.pets.find((pet) => pet.id === values.existingPetId);
        if (selectedPet) {
          const owner = current.owners.find((item) => item.id === selectedPet.ownerId);
          const nextOwner = owner ? patchOwner(owner, values) : owner;
          const nextPet = patchPet(selectedPet, selectedPet.ownerId, values);
          createdAppointment = createAppointmentRecord(selectedPet.id, values);

          return {
            ...current,
            owners: nextOwner ? current.owners.map((item) => (item.id === nextOwner.id ? nextOwner : item)) : current.owners,
            pets: current.pets.map((item) => (item.id === nextPet.id ? nextPet : item)),
            appointments: [...current.appointments, createdAppointment],
          };
        }
      }

      const matchingOwner = current.owners.find((owner) => normalizePhone(owner.phone) === normalizedPhone);
      const owner: Owner = matchingOwner
        ? patchOwner(matchingOwner, values)
        : {
            id: crypto.randomUUID(),
            fullName: values.ownerName.trim(),
            phone: displayPhone(values.phone),
            email: values.email.trim() || undefined,
          };
      const pet = patchPet(
        { id: crypto.randomUUID(), ownerId: owner.id, name: "", sex: "unknown" },
        owner.id,
        values,
      );
      createdAppointment = createAppointmentRecord(pet.id, values);

      return {
        owners: matchingOwner
          ? current.owners.map((item) => (item.id === owner.id ? owner : item))
          : [...current.owners, owner],
        pets: [...current.pets, pet],
        appointments: [...current.appointments, createdAppointment],
      };
    });

    return createdAppointment;
  }

  function updateAppointment(id: Id, values: AppointmentFormValues) {
    setData((current) => {
      const appointment = current.appointments.find((item) => item.id === id);
      if (!appointment) return current;

      const petId = values.existingPetId || appointment.petId;
      const pet = current.pets.find((item) => item.id === petId);
      if (!pet) return current;
      const owner = current.owners.find((item) => item.id === pet.ownerId);
      const nextPet = patchPet(pet, pet.ownerId, values);
      const nextOwner = owner ? patchOwner(owner, values) : owner;
      const start = combineDateAndTime(values.date, values.startTime);
      const end = new Date(start.getTime() + values.durationMinutes * 60_000);

      return {
        ...current,
        owners: nextOwner ? current.owners.map((item) => (item.id === nextOwner.id ? nextOwner : item)) : current.owners,
        pets: current.pets.map((item) => (item.id === nextPet.id ? nextPet : item)),
        appointments: current.appointments.map((item) =>
          item.id === id
            ? {
                ...item,
                petId,
                startsAt: start.toISOString(),
                endsAt: end.toISOString(),
                serviceType: values.serviceType || "other",
                customServiceName: values.serviceType === "other" ? values.customServiceName.trim() || undefined : undefined,
                servicePricePln: values.servicePricePln === "" ? undefined : Number(values.servicePricePln),
                notes: values.appointmentNotes.trim() || undefined,
              }
            : item,
        ),
      };
    });
  }

  function deleteAppointment(id: Id) {
    setData((current) => ({ ...current, appointments: current.appointments.filter((item) => item.id !== id) }));
  }

  function setAppointmentStatus(id: Id, status: AppointmentStatus) {
    setData((current) => ({
      ...current,
      appointments: current.appointments.map((item) => (item.id === id ? { ...item, status } : item)),
    }));
  }

  return { data, createAppointment, updateAppointment, deleteAppointment, setAppointmentStatus };
}
